import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Instagram, Download, Copy, Send, Sparkles, Check, Clock, Image, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const fadeUp = { hidden: { opacity: 0, y: 24 }, visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.08, duration: 0.5 } }) };

export default function InstagramManager() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [preview, setPreview] = useState(null);
  const [copied, setCopied] = useState(null);

  useEffect(() => { loadPosts(); }, []);

  const loadPosts = async () => {
    try {
      const res = await api.get("/admin/instagram/posts");
      setPosts(res.data || []);
    } catch { /* empty */ }
    finally { setLoading(false); }
  };

  const generatePost = async (mediaId) => {
    setGenerating(true);
    try {
      const res = await api.post(`/admin/instagram/generate-post/${mediaId}`);
      toast.success("Instagram post generated!");
      setPosts(prev => [res.data, ...prev]);
      setPreview(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || "Generation failed"); }
    finally { setGenerating(false); }
  };

  const copyCaption = (post) => {
    navigator.clipboard.writeText(post.full_text);
    setCopied(post.id);
    toast.success("Caption & hashtags copied!");
    setTimeout(() => setCopied(null), 2000);
  };

  const publishPost = async (postId) => {
    try {
      const res = await api.post(`/admin/instagram/publish/${postId}`);
      if (res.data.status === "published") {
        toast.success("Posted to Instagram!");
        loadPosts();
      } else if (res.data.status === "no_credentials") {
        toast.info("Instagram API not configured yet. Caption copied for manual posting.");
        const post = posts.find(p => p.id === postId);
        if (post) navigator.clipboard.writeText(post.full_text);
      } else {
        toast.error(res.data.message);
      }
    } catch { toast.error("Publish failed"); }
  };

  const downloadImage = (postId) => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/instagram/posts/${postId}/image`, "_blank");
  };

  return (
    <div data-testid="instagram-manager">
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-[10px] font-body uppercase tracking-[0.2em] text-pink-400/60">Instagram</p>
          <h2 className="font-heading text-xl text-white flex items-center gap-2">
            <Instagram size={20} className="text-pink-400" /> Post Manager
          </h2>
          <p className="text-white/30 text-xs font-body">@aristocratsja — {posts.length} posts generated</p>
        </div>
      </div>

      {posts.length === 0 && !loading && (
        <div className="text-center py-16 border border-white/5 bg-white/[0.01]">
          <Instagram size={40} className="text-white/10 mx-auto mb-4" />
          <p className="text-white/30 font-body text-sm mb-2">No Instagram posts generated yet.</p>
          <p className="text-white/20 font-body text-xs">Go to Gallery Manager and click the Instagram icon on any photo to create a post.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {posts.map((post, i) => (
          <motion.div key={post.id} initial="hidden" animate="visible" variants={fadeUp} custom={i} className="border border-white/5 bg-white/[0.015] overflow-hidden group" data-testid={`ig-post-${post.id}`}>
            {/* Image */}
            <div className="aspect-square bg-black relative overflow-hidden">
              <img src={post.ig_image_url} alt="" className="w-full h-full object-cover" loading="lazy" />
              <div className="absolute top-2 right-2">
                {post.status === "published" ? (
                  <span className="flex items-center gap-1 text-[9px] font-body px-2 py-0.5 bg-green-500/20 text-green-400"><Check size={8} /> Published</span>
                ) : (
                  <span className="flex items-center gap-1 text-[9px] font-body px-2 py-0.5 bg-gold/20 text-gold"><Clock size={8} /> Draft</span>
                )}
              </div>
            </div>

            {/* Caption preview */}
            <div className="p-4">
              <p className="text-white/60 text-xs font-body leading-relaxed line-clamp-3 mb-2">{post.caption}</p>
              <p className="text-pink-400/40 text-[9px] font-body leading-relaxed line-clamp-2">{post.hashtags}</p>
            </div>

            {/* Actions */}
            <div className="flex border-t border-white/5">
              <button onClick={() => setPreview(post)} className="flex-1 py-2.5 text-[10px] font-body text-white/30 hover:text-white hover:bg-white/5 transition-colors flex items-center justify-center gap-1" data-testid={`preview-${post.id}`}>
                <Image size={11} /> Preview
              </button>
              <button onClick={() => copyCaption(post)} className="flex-1 py-2.5 text-[10px] font-body text-white/30 hover:text-gold hover:bg-gold/5 transition-colors flex items-center justify-center gap-1 border-x border-white/5" data-testid={`copy-${post.id}`}>
                {copied === post.id ? <><Check size={11} /> Copied</> : <><Copy size={11} /> Copy</>}
              </button>
              {post.ig_storage_path && (
                <button onClick={() => downloadImage(post.id)} className="flex-1 py-2.5 text-[10px] font-body text-white/30 hover:text-blue-400 hover:bg-blue-400/5 transition-colors flex items-center justify-center gap-1 border-r border-white/5" data-testid={`download-${post.id}`}>
                  <Download size={11} /> Save
                </button>
              )}
              <button onClick={() => publishPost(post.id)} disabled={post.status === "published"} className="flex-1 py-2.5 text-[10px] font-body text-white/30 hover:text-pink-400 hover:bg-pink-400/5 transition-colors flex items-center justify-center gap-1 disabled:opacity-30" data-testid={`publish-${post.id}`}>
                <Send size={11} /> Post
              </button>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Preview Modal */}
      <AnimatePresence>
        {preview && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4" onClick={() => setPreview(null)} data-testid="ig-preview-modal">
            <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} exit={{ scale: 0.9 }} className="bg-luxury-charcoal border border-white/10 max-w-lg w-full overflow-hidden" onClick={e => e.stopPropagation()}>
              <div className="flex items-center justify-between px-4 py-3 border-b border-white/5">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 via-pink-500 to-orange-400 flex items-center justify-center"><Instagram size={14} className="text-white" /></div>
                  <div>
                    <p className="text-white text-xs font-body font-medium">aristocratsja</p>
                    <p className="text-white/30 text-[9px] font-body">Kingston, Jamaica</p>
                  </div>
                </div>
                <button onClick={() => setPreview(null)} className="text-white/30 hover:text-white"><X size={16} /></button>
              </div>
              <div className="aspect-square bg-black">
                <img src={preview.ig_image_url} alt="" className="w-full h-full object-cover" />
              </div>
              <div className="p-4 max-h-48 overflow-y-auto">
                <p className="text-white/70 text-xs font-body leading-relaxed whitespace-pre-wrap">{preview.caption}</p>
                <p className="text-blue-400/50 text-[10px] font-body mt-2 leading-relaxed">{preview.hashtags}</p>
              </div>
              <div className="flex border-t border-white/5">
                <button onClick={() => { copyCaption(preview); }} className="flex-1 py-3 text-xs font-body text-gold hover:bg-gold/10 transition-colors flex items-center justify-center gap-2">
                  <Copy size={14} /> Copy Caption & Hashtags
                </button>
                {preview.ig_storage_path && (
                  <button onClick={() => downloadImage(preview.id)} className="flex-1 py-3 text-xs font-body text-blue-400 hover:bg-blue-400/10 transition-colors flex items-center justify-center gap-2 border-l border-white/5">
                    <Download size={14} /> Download Image
                  </button>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export { InstagramManager };
